import { unwrapData } from '../utils/helperFn';

/**
 * The chat's Odin calls, handed to /api/odin-gateway so the key and the client secret
 * stay on the server (WEB-1880). Both the old and the new chat clients use this; they
 * differ only in how the server signs in, which is the `client` below.
 *
 * What comes back is shaped exactly as the chat already expects.
 */

type ClientName = 'default' | 'token';

const readAsBase64 = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(file);
  });

/* A file the customer attached travels as text, because the call itself is JSON. */
const asFilesAndFields = async (form: FormData) => {
  const files: { field: string; name: string; type: string; base64: string }[] = [];
  const fields: Record<string, string> = {};

  for (const [field, value] of (form as any).entries()) {
    if (value instanceof File) {
      files.push({
        field,
        name: value.name,
        type: value.type,
        base64: await readAsBase64(value),
      });
    } else {
      fields[field] = String(value);
    }
  }
  return { files, fields };
};

export const callGateway = async (
  client: ClientName,
  method: string,
  path: string,
  body?: any,
  options: Record<string, any> = {}
) => {
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;
  const attached = isForm ? await asFilesAndFields(body as FormData) : undefined;

  const response = await fetch('/api/odin-gateway', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client,
      method: method.toUpperCase(),
      path,
      ...(attached ? { files: attached.files, fields: attached.fields } : {}),
      ...(!isForm && body !== undefined ? { body } : {}),
      ...options,
    }),
  });

  const text = await response.text();
  let parsed: any;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    parsed = text;
  }

  if (!response.ok) {
    const error: any = new Error(parsed?.message || 'Odin call failed');
    error.response = { status: response.status, data: parsed };
    throw error;
  }

  return parsed && Object.prototype.hasOwnProperty.call(parsed, 'data')
    ? { ...parsed, data: unwrapData(parsed) }
    : { statusCode: 201, successful: true, data: unwrapData(parsed) };
};

export const wrapperFor = (client: ClientName) => ({
  get: (url: string, options = {}) => callGateway(client, 'get', url, undefined, options),
  post: (url: string, body: any, options = {}) => callGateway(client, 'post', url, body, options),
  put: (url: string, body: any, options = {}) => callGateway(client, 'put', url, body, options),
  patch: (url: string, body: any, options = {}) =>
    callGateway(client, 'patch', url, body, options),
  delete: (url: string, options = {}) => callGateway(client, 'delete', url, undefined, options),
});
